import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  getStats, seedData,
  getServices, createService, deleteService,
  getProjects, createProject, deleteProject,
  getTeam, createTeamMember, deleteTeamMember,
  getTestimonials, createTestimonial, deleteTestimonial,
  getEnquiries, markEnquiryRead, deleteEnquiry,
  getHeroImages, createHeroImage, deleteHeroImage,
  adminVerify,
} from "../lib/api";
import { LogOut, LayoutDashboard, Wrench, FolderOpen, Users, MessageSquare, Mail, Image } from "lucide-react";

const SECTIONS = [
  { key: "dashboard", label: "Dashboard", icon: LayoutDashboard },
  { key: "services", label: "Services", icon: Wrench },
  { key: "projects", label: "Projects", icon: FolderOpen },
  { key: "team", label: "Team", icon: Users },
  { key: "testimonials", label: "Testimonials", icon: MessageSquare },
  { key: "enquiries", label: "Enquiries", icon: Mail },
  { key: "hero", label: "Hero Images", icon: Image },
];

function toBase64(file) {
  return new Promise((res, rej) => {
    const reader = new FileReader();
    reader.onload = () => res(reader.result);
    reader.onerror = rej;
    reader.readAsDataURL(file);
  });
}

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [section, setSection] = useState("dashboard");
  const [stats, setStats] = useState({});
  const [services, setServices] = useState([]);
  const [projects, setProjects] = useState([]);
  const [team, setTeam] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [enquiries, setEnquiries] = useState([]);
  const [heroImages, setHeroImages] = useState([]);

  const [svcForm, setSvcForm] = useState({ name: "", icon: "🏛", description: "", order: 0 });
  const [projForm, setProjForm] = useState({ title: "", category: "residential", year: new Date().getFullYear(), description: "", images: [], coverImage: "" });
  const [teamForm, setTeamForm] = useState({ name: "", role: "", photo: "", order: 0 });
  const [testiForm, setTestiForm] = useState({ clientName: "", company: "", text: "", rating: 5 });
  const [heroForm, setHeroForm] = useState({ imageData: "", caption: "", order: 0 });

  const logout = () => {
    localStorage.removeItem("ark_admin_token");
    navigate("/admin/login");
  };

  useEffect(() => {
    adminVerify().catch(() => navigate("/admin/login"));
  }, [navigate]);

  const loadAll = useCallback(async () => {
    try {
      const [s, svc, proj, t, testi, enq, hero] = await Promise.all([
        getStats(), getServices(), getProjects(), getTeam(),
        getTestimonials(), getEnquiries(), getHeroImages(),
      ]);
      setStats(s); setServices(svc); setProjects(proj); setTeam(t);
      setTestimonials(testi); setEnquiries(enq); setHeroImages(hero);
    } catch {}
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);

  const handleSeed = async () => {
    try {
      await seedData();
      toast.success("Default services seeded!");
      loadAll();
    } catch { toast.error("Seed failed"); }
  };

  const handleAddService = async (e) => {
    e.preventDefault();
    try {
      await createService(svcForm);
      toast.success("Service added");
      setSvcForm({ name: "", icon: "🏛", description: "", order: 0 });
      loadAll();
    } catch { toast.error("Failed to add service"); }
  };

  const handleProjectImages = async (e) => {
    const files = Array.from(e.target.files);
    const b64s = await Promise.all(files.map(toBase64));
    setProjForm(f => ({ ...f, images: b64s, coverImage: b64s[0] || "" }));
  };

  const handleAddProject = async (e) => {
    e.preventDefault();
    try {
      await createProject(projForm);
      toast.success("Project added");
      setProjForm({ title: "", category: "residential", year: new Date().getFullYear(), description: "", images: [], coverImage: "" });
      loadAll();
    } catch { toast.error("Failed to add project"); }
  };

  const handleTeamPhoto = async (e) => {
    const b64 = await toBase64(e.target.files[0]);
    setTeamForm(f => ({ ...f, photo: b64 }));
  };

  const handleAddTeam = async (e) => {
    e.preventDefault();
    try {
      await createTeamMember(teamForm);
      toast.success("Team member added");
      setTeamForm({ name: "", role: "", photo: "", order: 0 });
      loadAll();
    } catch { toast.error("Failed to add team member"); }
  };

  const handleAddTesti = async (e) => {
    e.preventDefault();
    try {
      await createTestimonial(testiForm);
      toast.success("Testimonial added");
      setTestiForm({ clientName: "", company: "", text: "", rating: 5 });
      loadAll();
    } catch { toast.error("Failed to add testimonial"); }
  };

  const handleHeroImage = async (e) => {
    const b64 = await toBase64(e.target.files[0]);
    setHeroForm(f => ({ ...f, imageData: b64 }));
  };

  const handleAddHero = async (e) => {
    e.preventDefault();
    if (!heroForm.imageData) { toast.error("Select an image first"); return; }
    try {
      await createHeroImage(heroForm);
      toast.success("Hero image added");
      setHeroForm({ imageData: "", caption: "", order: 0 });
      loadAll();
    } catch { toast.error("Failed to upload image"); }
  };

  const inputCls = "w-full border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:border-gray-900 transition-colors";
  const btnCls = "text-xs tracking-widest uppercase font-medium bg-gray-900 text-white px-5 py-2.5 hover:bg-gray-700 transition-colors";
  const delBtnCls = "text-xs text-red-500 hover:text-red-700 underline";

  return (
    <div className="min-h-screen flex" style={{ background: "var(--color-bg-soft)" }}>
      {/* Sidebar */}
      <aside className="w-56 bg-white border-r border-gray-100 flex flex-col flex-shrink-0">
        <div className="px-6 py-5 border-b border-gray-100">
          <div className="text-sm tracking-widest font-bold text-gray-900" style={{ fontFamily: "var(--font-heading)" }}>
            ARK DESIGN
          </div>
          <div className="text-[9px] tracking-[0.3em] uppercase text-gray-400">Admin Panel</div>
        </div>
        <nav className="flex-1 py-4">
          {SECTIONS.map(({ key, label, icon: Icon }) => (
            <button
              key={key}
              onClick={() => setSection(key)}
              className={`w-full flex items-center gap-3 px-6 py-3 text-xs tracking-wider uppercase text-left transition-colors ${
                section === key
                  ? "bg-gray-900 text-white"
                  : "text-gray-600 hover:bg-gray-50"
              }`}
            >
              <Icon size={14} />
              {label}
            </button>
          ))}
        </nav>
        <button
          onClick={logout}
          className="flex items-center gap-3 px-6 py-4 text-xs tracking-wider uppercase text-gray-400 hover:text-red-500 border-t border-gray-100 transition-colors"
        >
          <LogOut size={14} /> Logout
        </button>
      </aside>

      {/* Main */}
      <main className="flex-1 p-8 overflow-y-auto">

        {/* Dashboard */}
        {section === "dashboard" && (
          <div>
            <h1 className="text-2xl font-normal text-gray-900 mb-8" style={{ fontFamily: "var(--font-heading)" }}>
              Dashboard
            </h1>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
              {[
                { label: "Services", value: stats.services ?? "—" },
                { label: "Projects", value: stats.projects ?? "—" },
                { label: "Team Members", value: stats.team ?? "—" },
                { label: "Unread Enquiries", value: stats.unread_enquiries ?? "—" },
              ].map(({ label, value }) => (
                <div key={label} className="bg-white border border-gray-100 p-6">
                  <div className="text-3xl font-semibold text-gray-900" style={{ fontFamily: "var(--font-heading)" }}>
                    {value}
                  </div>
                  <div className="text-xs tracking-widest uppercase text-gray-400 mt-1">{label}</div>
                </div>
              ))}
            </div>
            <button onClick={handleSeed} className={btnCls}>
              Seed Default Services
            </button>
          </div>
        )}

        {/* Services */}
        {section === "services" && (
          <div>
            <h1 className="text-2xl font-normal text-gray-900 mb-6" style={{ fontFamily: "var(--font-heading)" }}>
              Services
            </h1>
            <form onSubmit={handleAddService} className="bg-white border border-gray-100 p-6 mb-8 grid grid-cols-1 md:grid-cols-2 gap-4">
              <div><label className="text-xs tracking-widest uppercase text-gray-400 block mb-1">Name</label>
                <input required className={inputCls} value={svcForm.name} onChange={e => setSvcForm(f => ({ ...f, name: e.target.value }))} /></div>
              <div><label className="text-xs tracking-widest uppercase text-gray-400 block mb-1">Icon (emoji)</label>
                <input className={inputCls} value={svcForm.icon} onChange={e => setSvcForm(f => ({ ...f, icon: e.target.value }))} /></div>
              <div className="md:col-span-2"><label className="text-xs tracking-widest uppercase text-gray-400 block mb-1">Description</label>
                <textarea rows={2} className={inputCls} value={svcForm.description} onChange={e => setSvcForm(f => ({ ...f, description: e.target.value }))} /></div>
              <div><label className="text-xs tracking-widest uppercase text-gray-400 block mb-1">Sort Order</label>
                <input type="number" className={inputCls} value={svcForm.order} onChange={e => setSvcForm(f => ({ ...f, order: +e.target.value }))} /></div>
              <div className="flex items-end"><button type="submit" className={btnCls}>Add Service</button></div>
            </form>
            <div className="bg-white border border-gray-100">
              {services.map(svc => (
                <div key={svc.id} className="flex items-center gap-4 px-6 py-4 border-b border-gray-50 last:border-0">
                  <span className="text-2xl">{svc.icon}</span>
                  <div className="flex-1">
                    <div className="text-sm font-medium text-gray-900">{svc.name}</div>
                    <div className="text-xs text-gray-400">{svc.description}</div>
                  </div>
                  <button className={delBtnCls} onClick={async () => { await deleteService(svc.id); loadAll(); }}>Delete</button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Projects */}
        {section === "projects" && (
          <div>
            <h1 className="text-2xl font-normal text-gray-900 mb-6" style={{ fontFamily: "var(--font-heading)" }}>
              Projects
            </h1>
            <form onSubmit={handleAddProject} className="bg-white border border-gray-100 p-6 mb-8 grid grid-cols-1 md:grid-cols-2 gap-4">
              <div><label className="text-xs tracking-widest uppercase text-gray-400 block mb-1">Title</label>
                <input required className={inputCls} value={projForm.title} onChange={e => setProjForm(f => ({ ...f, title: e.target.value }))} /></div>
              <div><label className="text-xs tracking-widest uppercase text-gray-400 block mb-1">Category</label>
                <select className={inputCls} value={projForm.category} onChange={e => setProjForm(f => ({ ...f, category: e.target.value }))}>
                  {["residential","commercial","interior","landscape","urban"].map(c => <option key={c} value={c}>{c}</option>)}
                </select></div>
              <div><label className="text-xs tracking-widest uppercase text-gray-400 block mb-1">Year</label>
                <input type="number" className={inputCls} value={projForm.year} onChange={e => setProjForm(f => ({ ...f, year: +e.target.value }))} /></div>
              <div><label className="text-xs tracking-widest uppercase text-gray-400 block mb-1">Images</label>
                <input type="file" accept="image/*" multiple className="text-xs text-gray-500" onChange={handleProjectImages} /></div>
              <div className="md:col-span-2"><label className="text-xs tracking-widest uppercase text-gray-400 block mb-1">Description</label>
                <textarea rows={2} className={inputCls} value={projForm.description} onChange={e => setProjForm(f => ({ ...f, description: e.target.value }))} /></div>
              <div><button type="submit" className={btnCls}>Add Project</button></div>
            </form>
            <div className="bg-white border border-gray-100">
              {projects.map(proj => (
                <div key={proj.id} className="flex items-center gap-4 px-6 py-4 border-b border-gray-50 last:border-0">
                  {proj.coverImage && <img src={proj.coverImage} alt={proj.title} className="w-12 h-12 object-cover flex-shrink-0" />}
                  <div className="flex-1">
                    <div className="text-sm font-medium text-gray-900">{proj.title}</div>
                    <div className="text-xs text-gray-400 capitalize">{proj.category} · {proj.year}</div>
                  </div>
                  <button className={delBtnCls} onClick={async () => { await deleteProject(proj.id); loadAll(); }}>Delete</button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Team */}
        {section === "team" && (
          <div>
            <h1 className="text-2xl font-normal text-gray-900 mb-6" style={{ fontFamily: "var(--font-heading)" }}>
              Team
            </h1>
            <form onSubmit={handleAddTeam} className="bg-white border border-gray-100 p-6 mb-8 grid grid-cols-1 md:grid-cols-2 gap-4">
              <div><label className="text-xs tracking-widest uppercase text-gray-400 block mb-1">Name</label>
                <input required className={inputCls} value={teamForm.name} onChange={e => setTeamForm(f => ({ ...f, name: e.target.value }))} /></div>
              <div><label className="text-xs tracking-widest uppercase text-gray-400 block mb-1">Role</label>
                <input required className={inputCls} value={teamForm.role} onChange={e => setTeamForm(f => ({ ...f, role: e.target.value }))} /></div>
              <div><label className="text-xs tracking-widest uppercase text-gray-400 block mb-1">Photo</label>
                <input type="file" accept="image/*" className="text-xs text-gray-500" onChange={handleTeamPhoto} /></div>
              <div><label className="text-xs tracking-widest uppercase text-gray-400 block mb-1">Sort Order</label>
                <input type="number" className={inputCls} value={teamForm.order} onChange={e => setTeamForm(f => ({ ...f, order: +e.target.value }))} /></div>
              <div><button type="submit" className={btnCls}>Add Member</button></div>
            </form>
            <div className="bg-white border border-gray-100">
              {team.map(m => (
                <div key={m.id} className="flex items-center gap-4 px-6 py-4 border-b border-gray-50 last:border-0">
                  {m.photo ? <img src={m.photo} alt={m.name} className="w-10 h-10 rounded-full object-cover" /> : <div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center text-gray-400">👤</div>}
                  <div className="flex-1">
                    <div className="text-sm font-medium text-gray-900">{m.name}</div>
                    <div className="text-xs text-gray-400">{m.role}</div>
                  </div>
                  <button className={delBtnCls} onClick={async () => { await deleteTeamMember(m.id); loadAll(); }}>Delete</button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Testimonials */}
        {section === "testimonials" && (
          <div>
            <h1 className="text-2xl font-normal text-gray-900 mb-6" style={{ fontFamily: "var(--font-heading)" }}>
              Testimonials
            </h1>
            <form onSubmit={handleAddTesti} className="bg-white border border-gray-100 p-6 mb-8 grid grid-cols-1 md:grid-cols-2 gap-4">
              <div><label className="text-xs tracking-widest uppercase text-gray-400 block mb-1">Client Name</label>
                <input required className={inputCls} value={testiForm.clientName} onChange={e => setTestiForm(f => ({ ...f, clientName: e.target.value }))} /></div>
              <div><label className="text-xs tracking-widest uppercase text-gray-400 block mb-1">Company</label>
                <input className={inputCls} value={testiForm.company} onChange={e => setTestiForm(f => ({ ...f, company: e.target.value }))} /></div>
              <div className="md:col-span-2"><label className="text-xs tracking-widest uppercase text-gray-400 block mb-1">Testimonial</label>
                <textarea rows={3} required className={inputCls} value={testiForm.text} onChange={e => setTestiForm(f => ({ ...f, text: e.target.value }))} /></div>
              <div><label className="text-xs tracking-widest uppercase text-gray-400 block mb-1">Rating (1–5)</label>
                <input type="number" min={1} max={5} className={inputCls} value={testiForm.rating} onChange={e => setTestiForm(f => ({ ...f, rating: +e.target.value }))} /></div>
              <div className="flex items-end"><button type="submit" className={btnCls}>Add Testimonial</button></div>
            </form>
            <div className="bg-white border border-gray-100">
              {testimonials.map(t => (
                <div key={t.id} className="px-6 py-4 border-b border-gray-50 last:border-0">
                  <div className="flex justify-between items-start">
                    <div>
                      <div className="text-sm font-medium text-gray-900">{t.clientName} {t.company && <span className="text-gray-400">· {t.company}</span>}</div>
                      <div className="text-xs text-yellow-500 mt-0.5">{"★".repeat(t.rating)}</div>
                      <p className="text-xs text-gray-500 mt-1 line-clamp-2">{t.text}</p>
                    </div>
                    <button className={delBtnCls} onClick={async () => { await deleteTestimonial(t.id); loadAll(); }}>Delete</button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Enquiries */}
        {section === "enquiries" && (
          <div>
            <h1 className="text-2xl font-normal text-gray-900 mb-6" style={{ fontFamily: "var(--font-heading)" }}>
              Enquiries
            </h1>
            <div className="bg-white border border-gray-100">
              {enquiries.length === 0 && <div className="px-6 py-10 text-sm text-gray-400 text-center">No enquiries yet.</div>}
              {enquiries.map(enq => (
                <div key={enq.id} className={`px-6 py-4 border-b border-gray-50 last:border-0 ${!enq.read ? "bg-blue-50/30" : ""}`}>
                  <div className="flex justify-between items-start gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium text-gray-900">{enq.name}</span>
                        {!enq.read && <span className="text-[9px] tracking-wider uppercase bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full">New</span>}
                      </div>
                      <div className="text-xs text-gray-500 mt-0.5">{enq.email} {enq.phone && `· ${enq.phone}`}</div>
                      <p className="text-xs text-gray-600 mt-2">{enq.message}</p>
                      <div className="text-[10px] text-gray-300 mt-1">{new Date(enq.createdAt).toLocaleString()}</div>
                    </div>
                    <div className="flex flex-col gap-2 flex-shrink-0">
                      {!enq.read && (
                        <button className="text-xs text-blue-500 hover:text-blue-700 underline" onClick={async () => { await markEnquiryRead(enq.id); loadAll(); }}>
                          Mark Read
                        </button>
                      )}
                      <button className={delBtnCls} onClick={async () => { await deleteEnquiry(enq.id); loadAll(); }}>Delete</button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Hero Images */}
        {section === "hero" && (
          <div>
            <h1 className="text-2xl font-normal text-gray-900 mb-6" style={{ fontFamily: "var(--font-heading)" }}>
              Hero Images
            </h1>
            <form onSubmit={handleAddHero} className="bg-white border border-gray-100 p-6 mb-8 grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2"><label className="text-xs tracking-widest uppercase text-gray-400 block mb-1">Image</label>
                <input type="file" accept="image/*" className="text-xs text-gray-500" onChange={handleHeroImage} /></div>
              <div><label className="text-xs tracking-widest uppercase text-gray-400 block mb-1">Caption</label>
                <input className={inputCls} value={heroForm.caption} onChange={e => setHeroForm(f => ({ ...f, caption: e.target.value }))} /></div>
              <div><label className="text-xs tracking-widest uppercase text-gray-400 block mb-1">Sort Order</label>
                <input type="number" className={inputCls} value={heroForm.order} onChange={e => setHeroForm(f => ({ ...f, order: +e.target.value }))} /></div>
              <div><button type="submit" className={btnCls}>Upload Image</button></div>
            </form>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
              {heroImages.map(img => (
                <div key={img.id} className="relative group">
                  <img src={img.imageData} alt={img.caption} className="w-full aspect-video object-cover" />
                  <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                    <button className="text-white text-xs underline" onClick={async () => { await deleteHeroImage(img.id); loadAll(); }}>Delete</button>
                  </div>
                  {img.caption && <p className="text-xs text-gray-500 mt-1 truncate">{img.caption}</p>}
                </div>
              ))}
            </div>
          </div>
        )}

      </main>
    </div>
  );
}
